import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
} from "reactstrap";
import { MdVisibility, MdVisibilityOff } from "react-icons/md";
import { useTranslation } from "react-i18next";
import {
  useGetPaymentCredentialMine,
  useUpdatePaymentCredentialMine,
} from "api/payment-credentials/service";
import { getActiveColorStyle } from "utils/colorUtils";
import { useTheme2FormPage, Theme2FormFieldsStyle, THEME2_ADMIN_INSETS } from "hooks/useTheme2FormPage";

const emptyRepay = {
  client_id: "",
  client_secret: "",
  subdomain: "",
};

const PaymentSettings = ({ activeColor = "blue", shellThemeVariant = "1" }) => {
  const { t } = useTranslation();
  const { data, isLoading } = useGetPaymentCredentialMine();
  const updateMutation = useUpdatePaymentCredentialMine();

  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const {
    inputStyle,
    inputClassName,
    theme2FieldsCss,
    contentClassName,
    formFieldsClassName,
  } = useTheme2FormPage({
    shellThemeVariant,
    activeColorStyle,
    pageKey: "payment-settings",
    insetClass: THEME2_ADMIN_INSETS.paymentSettings,
  });

  const [repayForm, setRepayForm] = useState(emptyRepay);
  const [showClientSecret, setShowClientSecret] = useState(false);

  useEffect(() => {
    const payload = data?.data || data;
    const providers = payload?.providers || payload?.attributes?.providers || {};
    const repay = providers?.repay || {};
    setRepayForm({
      client_id: repay.client_id || "",
      client_secret: repay.client_secret || "",
      subdomain: repay.subdomain || "",
    });
  }, [data]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setRepayForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    updateMutation.mutate({
      providers: {
        repay: { ...repayForm },
      },
    });
  };

  if (isLoading) {
    return (
      <div className={contentClassName}>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "200px" }}
        >
          <div
            className="spinner-border"
            role="status"
            style={{ color: activeColorStyle.borderColor }}
          >
            <span className="sr-only">{t("paymentSettings.loading")}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={contentClassName}>
      <Theme2FormFieldsStyle css={theme2FieldsCss} />
      <style>
        {`
          .payment-settings-card-header.card-header {
            padding-top: 2px;
            padding-bottom: 0;
          }
          .payment-settings-card-header h2 {
            font-size: 16px !important;
            margin-bottom: 0;
            line-height: 1.2;
          }
          .payment-settings-page .payment-settings-card-header > .d-flex {
            margin-top: 3px;
          }
          .payment-settings-page .card .card-body {
            padding-top: 0px !important;
            padding-bottom: 0 !important;
            padding-left: 15px;
            padding-right: 15px;
          }
        `}
      </style>
      <Row>
        <Col md="12">
          <Card>
            <CardHeader className="payment-settings-card-header">
              <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between">
                <h2 className="mb-0">{t("routes.paymentSettings")}</h2>

                <div className="d-none d-lg-flex align-items-center">
                  <Button
                    color="primary"
                    size="sm"
                    type="submit"
                    form="payment-settings-form"
                    style={{ ...activeColorStyle, color: "white" }}
                    disabled={updateMutation.isPending}
                  >
                    {updateMutation.isPending ? (
                      <>
                        <Spinner size="sm" className="mr-1" />
                        {t("paymentSettings.updating")}
                      </>
                    ) : (
                      <>
                        <i className="tim-icons icon-check-2 mr-1" />
                        {t("paymentSettings.update")}
                      </>
                    )}
                  </Button>
                </div>

                <div className="d-flex d-lg-none flex-column w-100 mt-3">
                  <Button
                    color="primary"
                    type="submit"
                    form="payment-settings-form"
                    style={{ ...activeColorStyle, color: "white", padding: "12px" }}
                    disabled={updateMutation.isPending}
                    className="w-100"
                  >
                    {updateMutation.isPending
                      ? t("paymentSettings.updating")
                      : t("paymentSettings.update")}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardBody>
              <form
                id="payment-settings-form"
                onSubmit={handleSave}
                className={formFieldsClassName}
              >
                <h4 className="mb-3 mt-2">{t("paymentSettings.repayTitle")}</h4>
                <p className="text-muted mb-3" style={{ fontSize: "13px" }}>
                  {t("paymentSettings.repayHint")}
                </p>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>{t("paymentSettings.clientId")}</Label>
                      <Input
                        name="client_id"
                        type="text"
                        className={inputClassName()}
                        style={inputStyle}
                        value={repayForm.client_id}
                        onChange={handleChange}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>{t("paymentSettings.clientSecret")}</Label>
                      <div style={{ position: "relative" }}>
                        <Input
                          name="client_secret"
                          type={showClientSecret ? "text" : "password"}
                          className={inputClassName()}
                          style={inputStyle}
                          value={repayForm.client_secret}
                          onChange={handleChange}
                        />
                        <span
                          onClick={() => setShowClientSecret((v) => !v)}
                          style={{
                            position: "absolute",
                            right: "10px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            cursor: "pointer",
                            color: "#aaa",
                            zIndex: 10,
                          }}
                        >
                          {showClientSecret ? (
                            <MdVisibilityOff size={20} />
                          ) : (
                            <MdVisibility size={20} />
                          )}
                        </span>
                      </div>
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>{t("paymentSettings.subdomain")}</Label>
                      <Input
                        name="subdomain"
                        type="text"
                        className={inputClassName()}
                        style={inputStyle}
                        value={repayForm.subdomain}
                        onChange={handleChange}
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PaymentSettings;
